import React, { useCallback, useRef, useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
import {
  useAssetPreviewStore,
  AssetPreviewInstance,
  PreviewPanelBounds,
} from '../../store/assetPreviewStore';
import { AssetPreviewHeader } from './AssetPreviewHeader';
import { SpritePreview } from './SpritePreview';
import { VideoPreview } from './VideoPreview';
import { AudioPreview } from './AudioPreview';
import { PrefabPreview } from './PrefabPreview';
import { NoPreview } from './NoPreview';

interface AssetPreviewPanelProps {
  instance: AssetPreviewInstance;
}

type ResizeDirection =
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'ne'
  | 'nw'
  | 'se'
  | 'sw';

const RESIZE_HANDLE_SIZE = 8;
const MIN_WIDTH = 300;
const MIN_HEIGHT = 250;
const TOP_OFFSET = 32; // MenuBar height
const HEADER_HEIGHT = 32; // Panel header height (h-8 = 2rem = 32px)
const MIN_VISIBLE_WIDTH = 70; // Minimum visible panel width on screen

export const AssetPreviewPanel: React.FC<AssetPreviewPanelProps> = ({ instance }) => {
  const { bringToFront, setPanelBounds, activePreviewId } = useAssetPreviewStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const isActive = activePreviewId === instance.id;

  // Bring to front on mousedown (fires immediately, unlike click which waits for mouseup)
  const handlePanelMouseDown = useCallback(() => {
    bringToFront(instance.id);
  }, [bringToFront, instance.id]);

  // Drag handling — manipulate DOM directly during drag for responsiveness,
  // sync to store only on mouseup
  const handleStartDrag = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      e.preventDefault();
      setIsDragging(true);
      bringToFront(instance.id);

      const el = panelRef.current;
      const startX = e.clientX;
      const startY = e.clientY;
      const startBounds = { ...instance.bounds };
      let finalX = startBounds.x;
      let finalY = startBounds.y;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        const newX = startBounds.x + deltaX;
        const newY = startBounds.y + deltaY;

        // Constrain all edges to keep panel accessible
        const maxY = window.innerHeight - HEADER_HEIGHT;
        const minX = -startBounds.width + MIN_VISIBLE_WIDTH;
        const maxX = window.innerWidth - MIN_VISIBLE_WIDTH;
        finalX = Math.max(minX, Math.min(maxX, newX));
        finalY = Math.max(TOP_OFFSET, Math.min(maxY, newY));

        // Direct DOM manipulation — bypasses React re-render for instant feedback
        if (el) {
          el.style.left = `${finalX}px`;
          el.style.top = `${finalY}px`;
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Sync final position to store
        setPanelBounds(instance.id, { x: finalX, y: finalY });
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';
    },
    [bringToFront, instance.id, instance.bounds, setPanelBounds]
  );

  // Resize handling — manipulate DOM directly during resize for responsiveness,
  // sync to store only on mouseup
  const handleStartResize = useCallback(
    (direction: ResizeDirection) => (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      bringToFront(instance.id);

      const el = panelRef.current;
      const startX = e.clientX;
      const startY = e.clientY;
      const startBounds = { ...instance.bounds };
      let finalBounds: PreviewPanelBounds = { ...startBounds };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        const newBounds: PreviewPanelBounds = { ...startBounds };

        // Handle horizontal resize - no viewport constraint, only min width
        if (direction.includes('e')) {
          newBounds.width = Math.max(MIN_WIDTH, startBounds.width + deltaX);
        }
        if (direction.includes('w')) {
          const newWidth = Math.max(MIN_WIDTH, startBounds.width - deltaX);
          const actualDeltaX = startBounds.width - newWidth;
          newBounds.x = startBounds.x + actualDeltaX;
          newBounds.width = newWidth;
        }

        // Handle vertical resize - only top constrained (menubar), no bottom constraint
        if (direction.includes('s')) {
          newBounds.height = Math.max(MIN_HEIGHT, startBounds.height + deltaY);
        }
        if (direction.includes('n')) {
          const newHeight = Math.max(MIN_HEIGHT, startBounds.height - deltaY);
          const maxDeltaY = startBounds.height - MIN_HEIGHT;
          const actualDeltaY = Math.max(TOP_OFFSET - startBounds.y, Math.min(maxDeltaY, deltaY));
          newBounds.y = startBounds.y + actualDeltaY;
          newBounds.height = startBounds.height - actualDeltaY;
        }

        finalBounds = newBounds;

        // Direct DOM manipulation — bypasses React re-render for instant feedback
        if (el) {
          el.style.left = `${newBounds.x}px`;
          el.style.top = `${newBounds.y}px`;
          el.style.width = `${newBounds.width}px`;
          el.style.height = `${newBounds.height}px`;
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Sync final bounds to store
        setPanelBounds(instance.id, finalBounds);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Set appropriate cursor
      const cursorMap: Record<ResizeDirection, string> = {
        n: 'ns-resize',
        s: 'ns-resize',
        e: 'ew-resize',
        w: 'ew-resize',
        ne: 'nesw-resize',
        sw: 'nesw-resize',
        nw: 'nwse-resize',
        se: 'nwse-resize',
      };
      document.body.style.cursor = cursorMap[direction];
      document.body.style.userSelect = 'none';
    },
    [bringToFront, instance.id, instance.bounds, setPanelBounds]
  );

  // Render the appropriate preview content based on asset type
  const renderContent = () => {
    switch (instance.assetType) {
      case 'sprite':
        return <SpritePreview instanceId={instance.id} />;
      case 'video':
        return <VideoPreview instanceId={instance.id} />;
      case 'audio-music':
      case 'audio-sfx':
        return <AudioPreview instanceId={instance.id} />;
      case 'prefab':
        return <PrefabPreview instanceId={instance.id} />;
      default:
        return <NoPreview instanceId={instance.id} />;
    }
  };

  // Resize handle component
  const ResizeHandle: React.FC<{
    direction: ResizeDirection;
    className?: string;
  }> = ({ direction, className }) => (
    <div
      className={cn('absolute bg-transparent', className)}
      onMouseDown={handleStartResize(direction)}
      style={{
        cursor:
          direction === 'n' || direction === 's'
            ? 'ns-resize'
            : direction === 'e' || direction === 'w'
            ? 'ew-resize'
            : direction === 'ne' || direction === 'sw'
            ? 'nesw-resize'
            : 'nwse-resize',
      }}
    />
  );

  return (
    <div
      ref={panelRef}
      className={cn(
        'fixed flex flex-col',
        'bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl',
        'overflow-hidden',
        isActive && 'ring-1 ring-blue-500/50',
        (isDragging || isResizing) && 'transition-none',
        !isDragging && !isResizing && 'transition-shadow duration-100'
      )}
      style={{
        left: instance.bounds.x,
        top: instance.bounds.y,
        width: instance.bounds.width,
        height: instance.bounds.height,
        zIndex: instance.zIndex,
      }}
      onMouseDown={handlePanelMouseDown}
    >
      {/* Header */}
      <AssetPreviewHeader instance={instance} onStartDrag={handleStartDrag} />

      {/* Content */}
      <div className="flex-1 overflow-hidden">{renderContent()}</div>

      {/* Resize handles */}
      {/* Top */}
      <ResizeHandle
        direction="n"
        className={`top-0 left-${RESIZE_HANDLE_SIZE} right-${RESIZE_HANDLE_SIZE}`}
        style-override={{
          top: 0,
          left: RESIZE_HANDLE_SIZE,
          right: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
        }}
      />
      {/* Bottom */}
      <div
        className="absolute bg-transparent"
        style={{
          bottom: 0,
          left: RESIZE_HANDLE_SIZE,
          right: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'ns-resize',
        }}
        onMouseDown={handleStartResize('s')}
      />
      {/* Left */}
      <div
        className="absolute bg-transparent"
        style={{
          left: 0,
          top: RESIZE_HANDLE_SIZE,
          bottom: RESIZE_HANDLE_SIZE,
          width: RESIZE_HANDLE_SIZE,
          cursor: 'ew-resize',
        }}
        onMouseDown={handleStartResize('w')}
      />
      {/* Right */}
      <div
        className="absolute bg-transparent"
        style={{
          right: 0,
          top: RESIZE_HANDLE_SIZE,
          bottom: RESIZE_HANDLE_SIZE,
          width: RESIZE_HANDLE_SIZE,
          cursor: 'ew-resize',
        }}
        onMouseDown={handleStartResize('e')}
      />
      {/* Top-Left */}
      <div
        className="absolute bg-transparent"
        style={{
          top: 0,
          left: 0,
          width: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'nwse-resize',
        }}
        onMouseDown={handleStartResize('nw')}
      />
      {/* Top-Right */}
      <div
        className="absolute bg-transparent"
        style={{
          top: 0,
          right: 0,
          width: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'nesw-resize',
        }}
        onMouseDown={handleStartResize('ne')}
      />
      {/* Bottom-Left */}
      <div
        className="absolute bg-transparent"
        style={{
          bottom: 0,
          left: 0,
          width: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'nesw-resize',
        }}
        onMouseDown={handleStartResize('sw')}
      />
      {/* Bottom-Right */}
      <div
        className="absolute bg-transparent"
        style={{
          bottom: 0,
          right: 0,
          width: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'nwse-resize',
        }}
        onMouseDown={handleStartResize('se')}
      />
      {/* Top edge */}
      <div
        className="absolute bg-transparent"
        style={{
          top: 0,
          left: RESIZE_HANDLE_SIZE,
          right: RESIZE_HANDLE_SIZE,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'ns-resize',
        }}
        onMouseDown={handleStartResize('n')}
      />
    </div>
  );
};
